import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PULL_CONTENT_ID } from '@/components/common/PullToRefresh';
import { SnapshotStore, edgeShadowOf } from '@/components/common/swipeBackSnapshot';
import { installSnapshotCapture, mountUnderlay, paintUnderlay, unmountUnderlay, type Underlay } from '@/components/common/swipeBackUnderlay';

/**
 * 가장자리 스와이프 뒤로가기 — 화면 왼쪽 끝에서 오른쪽으로 밀면 이전 화면으로 돌아간다(iOS·Android 앱 공통).
 *
 * 앱은 운영 웹을 그대로 띄우는 웹뷰라 iOS 의 네이티브 뒤로가기 제스처가 꺼져 있다(결제 모드에서만 켜짐).
 * 네이티브 설정을 바꾸면 스토어 재배포가 필요하므로, 하이브리드 웹앱에서 가장 널리 쓰이는 Ionic 의 swipe-back 규칙을
 * 그대로 웹 코드로 구현했다 — 웹 배포만으로 설치된 앱에 바로 적용된다.
 *
 * 규칙(Ionic `swipe-back.ts` 와 동일):
 * - 왼쪽 가장자리 50px 안에서 시작한 터치만 후보, 가로로 10px 이상 움직이면 제스처로 확정
 * - 놓을 때 속도가 0.2px/ms 를 넘거나 화면 절반 이상 끌었으면 뒤로가기, 아니면 제자리로
 * - 마무리 애니메이션 시간 = 남은 거리 / 속도 (최대 540ms)
 *
 * 동작하지 않는 경우: 더 돌아갈 화면이 없을 때(히스토리 첫 화면), 메뉴·모달이 열려 있을 때, 세로 스크롤로 판정됐을 때,
 * `data-no-swipe-back` 영역에서 시작했을 때. 일반 모바일 브라우저는 브라우저 자체 제스처가 있어 앱에서만 켠다
 * (로컬 dev 빌드는 테스트용으로 터치 기기면 켠다).
 *
 * Android 제스처 내비게이션 기기는 시스템이 가장자리 터치를 먼저 가져가 기존 뒤로가기(backButton → history.back)로 처리되고,
 * 그때 웹에는 touchcancel 이 와서 이 제스처는 제자리로 돌아간다 — 이중 뒤로가기는 생기지 않는다.
 *
 * 끄는 동안 밑에는 이전 화면이 보인다(iOS 기본 뒤로가기처럼): 화면을 떠날 때(pushState 직전) 본문을 복사해 두고(최근 5개),
 * 제스처가 확정되면 바로 앞 항목의 복사본을 현재 화면 밑 왼쪽(-30%)에 깐다 — 아이폰 기본 뒤로가기처럼 현재 화면은 손가락을
 * 그대로(+x), 이전 화면은 느리게(-0.3·폭·(1-p)) 따라오고, 현재 화면 왼쪽 가장자리에 옅어지는 그림자(어둠 덮개 없음). 복사본이 없으면(새로 연 문서의 첫 이동 등) 지금처럼 빈 배경이다. 자세한 건 swipeBackUnderlay.ts.
 */

/** 제스처가 진행 중임을 다른 터치 제스처(풀투리프레시)에 알리는 body data 속성 */
export const SWIPE_BACK_ACTIVE_ATTR: string = 'swipeBack';

const EDGE_START_PX: number = 50;
const START_SLOP_PX: number = 10;
const COMPLETE_VELOCITY: number = 0.2;
const COMPLETE_FRACTION: number = 0.5;
const MAX_FINISH_MS: number = 540;
/** 아주 빠르게 튕겼을 때도 화면이 순간이동처럼 보이지 않게 하는 최소 시간 */
const MIN_FINISH_MS: number = 140;
/** history.back() 뒤 popstate 를 기다리는 최대 시간 — 안 오면 그냥 원위치시킨다 */
const POPSTATE_WAIT_MS: number = 600;
const EASING: string = 'cubic-bezier(0.22, 1, 0.36, 1)';
const isTouchDevice = (): boolean => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const isEnabled = (): boolean => isTouchDevice() && (Capacitor.isNativePlatform() || import.meta.env.DEV);

/** 가장자리 스와이프 뒤로가기를 쓰는 환경인지 — 폰 AI 시트(AiChatSheet)가 열린 동안 같은 제스처로 시트를 닫는 데 쓴다 */
export const isSwipeBackEnabled = (): boolean => isEnabled();

/**
 * 더 돌아갈 화면이 있는지.
 *
 * Navigation API(`navigation.canGoBack`, Android WebView·최신 iOS)가 있으면 그 값을 쓴다 — 문서가 통째로 다시 로드된 뒤에도
 * (결제 복귀·소셜 로그인 콜백·일반 a 링크) 웹뷰의 실제 이력을 정확히 안다.
 * 없으면 react-router v6 가 history.state.idx 에 기록하는 위치로 판단한다(0 이면 앱 안에서 더 돌아갈 화면이 없다).
 */
const canGoBack = (): boolean => {
    const navigationApi: { canGoBack?: unknown } | undefined = (window as { navigation?: { canGoBack?: unknown } }).navigation;
    if (typeof navigationApi?.canGoBack === 'boolean') return navigationApi.canGoBack;
    const idx: unknown = (window.history.state as { idx?: unknown } | null)?.idx;
    return typeof idx === 'number' ? idx > 0 : window.history.length > 1;
};

/**
 * 메뉴·모달·바텀시트가 열린 동안에는 밑에 깔린 화면을 넘기지 않는다.
 *
 * 단, 닫힌 채로 DOM 에 남아 있는 대화상자는 세지 않는다 — 항상 마운트해 두고 화면 밖으로 밀어 두는 방식(모바일 메뉴 등)에서
 * `[role="dialog"]` 하나만 보고 열렸다고 판정하면 스와이프 뒤로가기가 앱 전체에서 죽는다.
 */
const isOverlayOpen = (): boolean => {
    if (document.body.dataset.scrollLock === 'true') return true;
    if (document.querySelector('.MuiModal-root') !== null) return true;
    const dialogs: HTMLElement[] = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]'));
    return dialogs.some((dialog: HTMLElement) => dialog.getAttribute('aria-hidden') !== 'true' && dialog.closest('[inert]') === null);
};

const SwipeBack = () => {
    useEffect(() => {
        if (!isEnabled()) return undefined;

        let startX: number = 0;
        let startY: number = 0;
        let tracking: boolean = false;
        let locked: boolean = false;
        let busy: boolean = false;
        let delta: number = 0;
        let velocity: number = 0;
        let lastX: number = 0;
        let lastTime: number = 0;
        let frame: number = 0;
        let timer: number = 0;
        let underlay: Underlay | null = null;

        const content = (): HTMLElement | null => document.getElementById(PULL_CONTENT_ID);
        const snapshots: SnapshotStore<HTMLElement> = new SnapshotStore<HTMLElement>();
        const uninstallCapture: () => void = installSnapshotCapture(snapshots, content);

        const clearUnderlay = (): void => {
            if (!underlay) return;
            unmountUnderlay(underlay, content());
            underlay = null;
        };

        const paint = (value: number, durationMs: number): void => {
            const target: HTMLElement | null = content();
            const transition: string = durationMs > 0 ? `${durationMs}ms ${EASING}` : '';
            if (target) {
                target.style.transition = durationMs > 0 ? `transform ${transition}` : 'none';
                target.style.transform = value > 0 ? `translate3d(${value}px, 0, 0)` : '';
                // 복사본이 있으면 경계 그림자는 바탕 판이 맡는다(paintUnderlay), 빈 배경일 때만 본문에 얇은 그림자
                target.style.boxShadow = underlay ? '' : edgeShadowOf(value);
            }
            if (underlay) paintUnderlay(underlay, value, durationMs > 0 ? transition : '');
        };

        const release = (): void => {
            delete document.body.dataset[SWIPE_BACK_ACTIVE_ATTR];
            busy = false;
        };

        const goBack = (): void => {
            let done: boolean = false;
            const finish = (): void => {
                if (done) return;
                done = true;
                window.removeEventListener('popstate', finish);
                window.clearTimeout(timer);
                // 들어오는 애니메이션 없이 바로 실제 화면으로 바꾼다 — 복사본이 있었으면 이미 밑에 다 보이고 있었고, 없었으면 빈 배경 위로
                // 이전 화면이 그대로 나타난다. popstate 처리 뒤 React 가 같은 작업(마이크로태스크) 안에서 이전 화면을 그리므로 빈 틈이 보이지 않는다.
                clearUnderlay();
                paint(0, 0);
                const target: HTMLElement | null = content();
                if (target) target.style.transition = '';
                release();
            };
            window.addEventListener('popstate', finish);
            timer = window.setTimeout(finish, POPSTATE_WAIT_MS);
            window.history.back();
        };

        const settle = (complete: boolean): void => {
            const width: number = window.innerWidth;
            const missing: number = complete ? width - delta : delta;
            const speed: number = Math.abs(velocity);
            const natural: number = speed > 0.01 ? missing / speed : MAX_FINISH_MS;
            const duration: number = Math.max(MIN_FINISH_MS, Math.min(MAX_FINISH_MS, natural));
            busy = true;
            paint(complete ? width : 0, duration);
            timer = window.setTimeout(() => {
                if (complete) {
                    goBack();
                    return;
                }
                clearUnderlay();
                const target: HTMLElement | null = content();
                if (target) target.style.transition = '';
                release();
            }, duration);
        };

        const onTouchStart = (event: TouchEvent): void => {
            if (busy || event.touches.length !== 1) return;
            const touch: Touch = event.touches[0];
            if (touch.clientX > EDGE_START_PX) return;
            if (!canGoBack() || isOverlayOpen()) return;
            const origin: Element | null = event.target instanceof Element ? event.target : null;
            if (origin?.closest('[data-no-swipe-back], input[type="range"]')) return;
            tracking = true;
            locked = false;
            startX = touch.clientX;
            startY = touch.clientY;
            lastX = startX;
            lastTime = event.timeStamp;
            delta = 0;
            velocity = 0;
        };

        const onTouchMove = (event: TouchEvent): void => {
            if (!tracking) return;
            const touch: Touch = event.touches[0];
            const dx: number = touch.clientX - startX;
            const dy: number = touch.clientY - startY;
            if (!locked) {
                // 세로로 먼저 움직였으면 스크롤 — 이 터치는 끝까지 건드리지 않는다
                if (Math.abs(dy) > START_SLOP_PX && Math.abs(dy) >= Math.abs(dx)) {
                    tracking = false;
                    return;
                }
                if (dx < START_SLOP_PX || dx <= Math.abs(dy)) return;
                locked = true;
                document.body.dataset[SWIPE_BACK_ACTIVE_ATTR] = 'true';
                // 이전 화면 복사본을 현재 화면 밑 왼쪽(-30%)에 깐다(없으면 빈 배경) — 이후 느리게 따라온다
                clearUnderlay();
                underlay = mountUnderlay(snapshots, content());
            }
            const elapsed: number = event.timeStamp - lastTime;
            if (elapsed > 0) {
                // 직전 구간 속도에 가중 평균을 줘 손끝 떨림으로 방향이 튀지 않게 한다
                const instant: number = (touch.clientX - lastX) / elapsed;
                velocity = velocity * 0.6 + instant * 0.4;
                lastX = touch.clientX;
                lastTime = event.timeStamp;
            }
            delta = Math.max(0, Math.min(window.innerWidth, dx));
            if (!frame) {
                frame = window.requestAnimationFrame(() => {
                    frame = 0;
                    if (locked && !busy) paint(delta, 0);
                });
            }
            // 가로로 끄는 동안 브라우저의 스크롤·캐러셀 넘김이 끼어들지 않게 막는다 (passive: false 필요)
            if (event.cancelable) event.preventDefault();
        };

        const end = (cancelled: boolean): void => {
            if (!tracking) return;
            tracking = false;
            if (!locked) return;
            locked = false;
            if (frame) {
                window.cancelAnimationFrame(frame);
                frame = 0;
            }
            const complete: boolean =
                !cancelled && velocity >= 0 && (velocity > COMPLETE_VELOCITY || delta > window.innerWidth * COMPLETE_FRACTION);
            settle(complete);
        };

        const onTouchEnd = (): void => end(false);
        // 시스템 제스처(Android 뒤로가기 등)가 터치를 가져가면 제자리로 — 뒤로가기는 시스템 쪽이 처리한다
        const onTouchCancel = (): void => end(true);

        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd, { passive: true });
        document.addEventListener('touchcancel', onTouchCancel, { passive: true });
        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            document.removeEventListener('touchcancel', onTouchCancel);
            if (frame) window.cancelAnimationFrame(frame);
            window.clearTimeout(timer);
            paint(0, 0);
            clearUnderlay();
            uninstallCapture();
            release();
        };
    }, []);

    return null;
};

export default SwipeBack;
